import { create } from "zustand"

import "../graphql/payableApprovalList.query"

import {
  GetPayableApprovalInvoiceListDocument,
  type GetPayableApprovalInvoiceListQuery,
  type Invoice,
  type InvoiceListInput,
  type InvoiceSortField,
  InvoiceStatus,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type PayableApprovalsSearchFilters = SearchFilters & {
  field?: InvoiceSortField
  order?: SortingOrder
  dueDateFrom?: string
  dueDateTo?: string
}

export const deduplicatePayableApprovals = (newRows: Invoice[], existing: Invoice[]): Invoice[] => {
  const seen = new Set(existing.map((invoice) => invoice.invoiceId))
  return [...existing, ...newRows.filter((invoice) => !seen.has(invoice.invoiceId))]
}

export const getPayableApprovalsSearchInput = (
  filters: PayableApprovalsSearchFilters,
  offset: number,
  limit: number
): InvoiceListInput & { filter: NonNullable<InvoiceListInput["filter"]> } => {
  const { field, order, query, dueDateFrom, dueDateTo } = filters

  return {
    offset,
    limit,
    sort: field && order ? { field, order } : undefined,
    filter: {
      query: query || undefined,
      statuses: [InvoiceStatus.PendingApproval],
      dueDateFrom,
      dueDateTo,
    },
  }
}

interface ExtractedPayableApprovalsResponse {
  __typename: string
  data: Invoice[]
  error?: { message: string }
}

export const extractPayableApprovalsResponse = (
  data: GetPayableApprovalInvoiceListQuery
): ExtractedPayableApprovalsResponse => {
  const result = data.invoice.list
  if (result.__typename === "InvoiceListSuccess") {
    return {
      __typename: result.__typename,
      data: result.invoices as Invoice[],
    }
  }
  if (result.__typename === "InvoiceListFailure") {
    return {
      __typename: result.__typename,
      data: [],
      error: result.error,
    }
  }
  // Surface an unknown __typename so an empty table never masks a backend regression.
  const unknownTypename = (result as { __typename: string }).__typename
  return {
    __typename: unknownTypename,
    data: [],
    error: { message: `Unexpected response type: ${unknownTypename}` },
  }
}

export const usePayableApprovalsSearchStore = create(
  createSearchStore<Invoice, PayableApprovalsSearchFilters, GetPayableApprovalInvoiceListQuery>({
    documentNode: GetPayableApprovalInvoiceListDocument,
    deduplicateData: deduplicatePayableApprovals,

    getQueryInput: getPayableApprovalsSearchInput,

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: extractPayableApprovalsResponse,

    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
